import React, { useState, useCallback } from 'react';
import type { FormField } from '@/types/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Upload, X, File, AlertCircle, CheckCircle } from 'lucide-react';

interface FileUploadFieldProps {
  field: FormField;
  value?: File | string;
  onChange?: (value: File | null) => void;
  preview?: boolean;
}

interface UploadedFile {
  file: File;
  id: string;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  field,
  value,
  onChange,
  preview = false
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const maxSizeBytes = field.validation?.maxSizeBytes || 10485760; // 10MB default
  const allowedTypes = field.validation?.allowedTypes || ['*/*'];

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > maxSizeBytes) {
      return `File size exceeds ${formatFileSize(maxSizeBytes)}`;
    }

    // Check file type
    if (!allowedTypes.includes('*/*')) {
      const isAllowed = allowedTypes.some(type => {
        if (type.endsWith('/*')) {
          // Handle MIME type wildcards like "image/*"
          const baseType = type.slice(0, -2);
          return file.type.startsWith(baseType);
        } else if (type.startsWith('.')) {
          // Handle file extensions like ".pdf"
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        } else {
          // Handle exact MIME types like "application/pdf"
          return file.type === type;
        }
      });

      if (!isAllowed) {
        return `File type not allowed. Accepted types: ${allowedTypes.join(', ')}`;
      }
    }

    return null;
  }, [maxSizeBytes, allowedTypes]);

  const simulateUpload = (uploadedFile: UploadedFile): Promise<void> => {
    return new Promise((resolve, reject) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          
          setUploadedFiles(prev => prev.map(f => 
            f.id === uploadedFile.id 
              ? { ...f, status: 'success', progress: 100 }
              : f
          ));
          resolve();
        } else {
          setUploadedFiles(prev => prev.map(f => 
            f.id === uploadedFile.id 
              ? { ...f, progress }
              : f
          ));
        }
      }, 100);
    });
  };

  const handleFileSelect = useCallback(async (files: FileList) => {
    const file = files[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      const errorFileId = Math.random().toString(36).substr(2, 9);
      setUploadedFiles(prev => [...prev, {
        file,
        id: errorFileId,
        status: 'error',
        progress: 0,
        error: validationError
      }]);
      return;
    }

    // Create uploaded file entry
    const uploadFileId = Math.random().toString(36).substr(2, 9);
    const uploadedFile: UploadedFile = {
      file,
      id: uploadFileId,
      status: 'uploading',
      progress: 0
    };

    setUploadedFiles(prev => [...prev, uploadedFile]);

    try {
      // Simulate file upload
      if (preview) {
        await simulateUpload(uploadedFile);
        onChange?.(file);
      } else {
        // In a real implementation, this would upload to your server
        // and return a file ID/URL to store in the form data
        onChange?.(file);
      }
    } catch (error) {
      setUploadedFiles(prev => prev.map(f => 
        f.id === uploadedFile.id 
          ? { ...f, status: 'error', error: 'Upload failed' }
          : f
      ));
    }
  }, [validateFile, onChange, preview]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    onChange?.(null);
  };

  if (!preview) {
    return (
      <div className="space-y-2">
        <Label>File Upload Configuration</Label>
        <div className="text-sm text-muted-foreground">
          <p>Allowed types: {allowedTypes.join(', ')}</p>
          <p>Max size: {formatFileSize(maxSizeBytes)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg transition-colors ${
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="p-6 text-center">
          <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground mb-2">
            {field.placeholder || 'Drag and drop a file here, or click to select'}
          </p>
          <Button variant="outline" className="mb-2">
            Choose File
          </Button>
          <Input
            type="file"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={handleInputChange}
            accept={allowedTypes.join(',')}
          />
        </div>
      </div>

      {/* File Constraints */}
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">Max: {formatFileSize(maxSizeBytes)}</Badge>
        {allowedTypes.length > 0 && allowedTypes[0] !== '*/*' && (
          <Badge variant="outline">Types: {allowedTypes.slice(0, 2).join(', ')}
            {allowedTypes.length > 2 && ` +${allowedTypes.length - 2} more`}
          </Badge>
        )}
      </div>

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm">Uploaded Files</Label>
          {uploadedFiles.map((uploadedFile) => (
            <div
              key={uploadedFile.id}
              className="flex items-center gap-3 p-3 border rounded-lg bg-card"
            >
              <File className="h-8 w-8 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {uploadedFile.file.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(uploadedFile.file.size)}
                </p>
                
                {uploadedFile.status === 'uploading' && (
                  <div className="w-full bg-secondary rounded-full h-2 mt-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadedFile.progress}%` }}
                    />
                  </div>
                )}
                
                {uploadedFile.status === 'error' && uploadedFile.error && (
                  <p className="text-xs text-red-500 mt-1">{uploadedFile.error}</p>
                )}
              </div>
              
              {uploadedFile.status === 'success' && (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              
              {uploadedFile.status === 'error' && (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFile(uploadedFile.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};